import React, { useState, useCallback, useEffect } from 'react';
import { Referrer, useSignupStore } from 'src/onboarding/store/signup.store';
import { Nullable } from 'src/types/nullable.type';
import { useSegmentTrackedLink } from 'src/utils/segment-tracked-link.hook';
import * as Yup from 'yup';
import { FormikProps, useFormik } from 'formik';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { apiSignup, apiCheckEmail } from 'src/onboarding/api/auth.api';
import { Button } from 'src/common/button';

import googleLogo from '../../../logos/google.svg';
import microsoftLogo from '../../../logos/microsoft.svg';

export const urlToProviderSSO = (providerName: String, invite: Nullable<any>, referrer: Nullable<Referrer>, template_id: Nullable<string>, cancelInvite: Nullable<boolean | undefined> = false) => {
  const url = new URL(`/oauth/login/${providerName}`, window.location.origin);
  const searchParamas = url.searchParams;
  if (invite) {
    searchParamas.append('invite_token', invite.uuid);
  }
  if (referrer && referrer.slug) {
    searchParamas.append('referral', referrer.slug);
  }
  if (template_id) {
    searchParamas.append('template_id', template_id);
  }
  if(cancelInvite){
    searchParamas.append('cancel_invite', '1')
  }

  return url.href;
};

// Email validation schema
const emailValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Required'),
});

// Full registration validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email address').required('Required'),
  password: Yup.string()
    .required('Required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
});

interface SignupProps {
  cancelInvite?: boolean;
  onSignupSuccess?: (response: Record<string, string>) => void;
}

const PasswordStrengthMeter = ({ password }: { password: string }) => {
  const calculateStrength = (password: string): number => {
    let score = 0;
    
    if (!password) return score;
    
    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    
    // Complexity checks
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    
    return Math.min(score, 5); // Max score of 5
  };

  const strength = calculateStrength(password);
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
  const strengthColors = [
    'bg-red-500',
    'bg-red-500',
    'bg-yellow-500',
    'bg-yellow-300',
    'bg-green-400',
    'bg-green-500'
  ];

  return (
    <div className="w-full mt-1">
      <div className="flex w-full h-2 rounded-full bg-gray-200 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <div 
            key={i} 
            className={`h-full w-1/5 ${i < strength ? strengthColors[strength] : 'bg-gray-200'}`} 
          />
        ))}
      </div>
      <div className="text-xs mt-1 text-gray-500">
        {strength > 0 ? strengthLabels[strength] : 'No password entered'}
      </div>
    </div>
  );
};

const PasswordGuidelines = ({ password }: { password: string }) => {
  const guidelines = [
    { test: (p: string) => p.length >= 8, text: 'At least 8 characters' },
    { test: (p: string) => /[A-Z]/.test(p), text: 'At least one uppercase letter' },
    { test: (p: string) => /[a-z]/.test(p), text: 'At least one lowercase letter' },
    { test: (p: string) => /[0-9]/.test(p), text: 'At least one number' },
    { test: (p: string) => /[^A-Za-z0-9]/.test(p), text: 'At least one special character' },
  ];

  return (
    <div className="mt-2 text-xs">
      <p className="text-gray-500 mb-1">Password must have:</p>
      <ul className="space-y-1">
        {guidelines.map((guideline, index) => (
          <li 
            key={index} 
            className={`flex items-center ${guideline.test(password) ? 'text-green-600' : 'text-gray-500'}`}
          >
            <svg 
              className={`mr-1 h-3 w-3 ${guideline.test(password) ? 'text-green-600' : 'text-gray-400'}`} 
              fill="currentColor" 
              viewBox="0 0 20 20" 
              xmlns="http://www.w3.org/2000/svg"
            >
              {guideline.test(password) ? (
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              ) : (
                <circle cx="10" cy="10" r="7.5" stroke="currentColor" fill="none" strokeWidth="1.5" />
              )}
            </svg>
            {guideline.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

const SignupForm: React.FC<SignupProps> = ({cancelInvite, onSignupSuccess}) => {
  const { invite, referrer, template_id } = useSignupStore();
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showNamePassword, setShowNamePassword] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [hasInvite, setHasInvite] = useState(false);
  const [inviteOrg, setInviteOrg] = useState<string | null>(null);
  const [emailExists, setEmailExists] = useState(false);

  const googleSignupLinkRef = useSegmentTrackedLink('Create Account Clicked', {
    provider: 'google',
  });
  const microsoftSignupLinkRef = useSegmentTrackedLink('Create Account Clicked', {
    provider: 'microsoft',
  });

  const handleSignupSuccess = useCallback((response: Record<string, string>) => {
    if (onSignupSuccess) {
      onSignupSuccess(response);
    } else {
      window.location.replace(response.redirect || '/');
    }
  }, [onSignupSuccess]);

  const emailFormik = useFormik({
    initialValues: { email: '' },
    validationSchema: emailValidationSchema,
    onSubmit: async (values) => {
      setCheckingEmail(true);
      setErrorMessage(null);
      setEmailExists(false);
      
      try {
        const response = await apiCheckEmail(values.email);
        
        if (response.exists) {
          setEmailExists(true);
          setErrorMessage('An account with this email already exists.');
          setCheckingEmail(false);
          return;
        }
        
        setEmailValue(values.email);
        setShowNamePassword(true);
        setHasInvite(response.has_invite);
        setInviteOrg(response.invite_organization);
      } catch (error: any) {
        setErrorMessage(error.message || 'Failed to check email. Please try again.');
      } finally {
        setCheckingEmail(false);
      }
    }
  });

  const navigateToLogin = () => {
    // Preserve any template_id or other query params when redirecting
    const queryParams = new URLSearchParams(window.location.search);
    // Keep the email to pre-fill on login page
    if (emailFormik.values.email) {
      queryParams.set('email', emailFormik.values.email);
    }
    window.location.href = `/login${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  };

  const formikParams = {
    initialValues: { name: '', email: emailValue, password: '' },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values: Record<string, string>) => {
      setSubmitting(true);
      setErrorMessage(null);
      
      const signupParams = { 
        ...values, 
        invite_token: invite?.uuid,
        referral: referrer?.slug,
        template_id
      };
      
      apiSignup(signupParams)
        .then(handleSignupSuccess)
        .catch((response) => {
          setErrorMessage(response.message);
          setSubmitting(false);
        });
    },
  };

  const renderEmailForm = () => {
    return (
      <form onSubmit={emailFormik.handleSubmit} className="w-full">
        <div className="flex flex-col space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
              <span className="text-red-600">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              value={emailFormik.values.email}
              onChange={emailFormik.handleChange}
              onBlur={emailFormik.handleBlur}
            />
            {emailFormik.touched.email && emailFormik.errors.email && (
              <div className="mt-1 text-sm text-red-600">{emailFormik.errors.email}</div>
            )}
          </div>
          
          {emailExists ? (
            <div className="flex flex-col space-y-4">
              <div className="bg-gray-50 text-gray-700 text-sm p-4 rounded-md">
                <p>An account with this email already exists.</p>
                <p>Would you like to sign in instead?</p>
              </div>
              <Button
                className="h-10 w-full"
                type="button"
                variant="primary"
                size="regular"
                onClick={navigateToLogin}
                label="Go to sign in"
              />
              <button 
                type="button"
                onClick={() => {
                  setEmailExists(false);
                  setErrorMessage(null);
                  emailFormik.setFieldValue('email', '');
                }}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <div>
              <Button
                className="h-10 w-full"
                type="submit"
                variant="primary"
                size="regular"
                disabled={checkingEmail || !emailFormik.isValid}
                icon={
                  checkingEmail ? (
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : undefined
                }
                label={checkingEmail ? 'Checking...' : 'Continue'}
              />
              {errorMessage && !emailExists && (
                <div className="mt-2 text-sm text-red-600">{errorMessage}</div>
              )}
            </div>
          )}
        </div>
      </form>
    );
  };

  const renderEmailPasswordForm = () => {
    return (
      <JourneyFormik {...formikParams}>
        {({ values }: FormikProps<Record<string, string>>) => {
          const isFormValid = validationSchema.isValidSync(values);

          return (
            <JourneyForm className='flex flex-col space-y-6'>
              {hasInvite && inviteOrg && (
                <div className="text-sm bg-blue-50 text-blue-700 p-3 rounded-md">
                  You have a pending invitation to join <strong>{inviteOrg}</strong>. Complete your account to accept.
                </div>
              )}
              
              <div className='flex flex-col space-y-4'>
              
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email address
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    disabled
                    value={emailValue}
                    className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                  />
                  {/* <button 
                    type="button" 
                    onClick={() => setShowNamePassword(false)} 
                    className="mt-1 text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    Change email
                  </button> */}
                </div>
                <div>
                <FormikTextInput
                  id='name'
                  required
                  name='name'
                  type='text'
                  label='Full name'
                  errorClasses='mt-1'
                />
                </div>
                <div>
                  <FormikTextInput
                    id='password'
                    required
                    name='password'
                    type='password'
                    label='Password'
                    errorClasses='mt-1'
                  />
                  <PasswordStrengthMeter password={values.password} />
                  <PasswordGuidelines password={values.password} />
                </div>
              </div>
              <div className='flex flex-col space-y-2'>
                {errorMessage && <div className='text-bedrock-red text-sm'>{errorMessage}</div>}
                <Button
                  className='h-10'
                  type='submit'
                  variant='primary'
                  size='regular'
                  disabled={submitting || !isFormValid}
                  icon={
                    submitting ? (
                      <svg
                        className='animate-spin h-4 w-4'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                    ) : undefined
                  }
                  label={submitting ? '' : 'Create account'}
                />
              </div>
            </JourneyForm>
          );
        }}
      </JourneyFormik>
    );
  };

  const renderSSOOptions = () => {
    return (
      <div>
        <div className='flex flex-col space-y-4 mt-8'>
          <a
            ref={googleSignupLinkRef}
            href={urlToProviderSSO('google', invite, referrer, template_id, cancelInvite)}
            className='btn-bedrock-secondary flex justify-center items-center space-x-2'
          >
            <img src={googleLogo} className='inline w-5 h-5' alt='Signup with Google' />
            <span>Create account with Google</span>
          </a>
          <a
            ref={microsoftSignupLinkRef}
            href={urlToProviderSSO('microsoft', invite, referrer, template_id, cancelInvite)}
            className='btn-bedrock-secondary flex justify-center items-center space-x-2'
          >
            <img src={microsoftLogo} className='inline w-5 h-5' alt='Signup with Microsoft' />
            <span>Create account with Microsoft</span>
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className='w-full'>
      {invite && (
        <div className='mt-6 text-bedrock-black text-bedrock-p'>
          {invite.created_by_user.name} invited you to the {invite.organization.name} organization on Journey.
        </div>
      )}
      <div className='mt-10 space-y-6'>
        {showNamePassword ? renderEmailPasswordForm() : renderEmailForm()}
        
        {!showNamePassword && (
          <>
            <div className='relative flex items-center py-2'>
              <div className='flex-grow border-t border-gray-300'></div>
              <span className='flex-shrink mx-4 text-gray-400'>or</span>
              <div className='flex-grow border-t border-gray-300'></div>
            </div>
            {renderSSOOptions()}
          </>
        )}
        
        <div className='flex mt-4'>
          <div className='text-bedrock-black text-bedrock-p-small'>
            {`You agree to `}
            <a
              href='https://www.journey.io/terms'
              target='_blank'
              rel='noopener noreferrer'
              className='text-bedrock-dark-gray hover:text-bedrock-gray-medium'
            >
              Terms of Service
            </a>
            {` and `}
            <a
              href='https://www.journey.io/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              className='text-bedrock-dark-gray hover:text-bedrock-gray-medium'
            >
              Privacy Policy
            </a>
            {` by creating an account`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
